import { Card, Grid, Stack, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { PERMISSIONS_NAME } from "lib/constants";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { checkPermission } from "utils/HelperFunctions";
import useWorkspaceSubscription from "hooks/useWorkspaceSubscription";
import { useLocation, useNavigate } from "react-router-dom";
import Templates from "../Templates";
import AssignedUsers from "../AssignedUsers";
import ManageKPIs from "../ManageKPIs";

const prebuiltComponents = {
  "/kpi/templates": Templates,
  "/kpi/manage-user-kpis": AssignedUsers,
  "/kpi/manage-kpis": ManageKPIs,
};

const SIDE_NAV_ITEMS = [
  { label: "Templates", href: "/kpi/templates", permissionName: PERMISSIONS_NAME.MANAGE_KPIS },
  {
    label: "Manage Users",
    href: "/kpi/manage-user-kpis",
    permissionName: PERMISSIONS_NAME.MANAGE_KPIS,
  },
  { label: "Manage KPIs", href: "/kpi/manage-kpis", permissionName: PERMISSIONS_NAME.MANAGE_KPIS },
];

function KpiTabView() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { subscription } = useWorkspaceSubscription();
  const [isRequestLoading, setIsRequestLoading] = useState(true);

  const companyAdmin = useSelector((state) => state.loginUser.loginUserState);

  const [sidenavItems, setSideNavItems] = useState(
    SIDE_NAV_ITEMS.filter((item) =>
      checkPermission(item.permissionName, companyAdmin.userPermission)
    )
  );
  const selectedCategory = prebuiltComponents[pathname] ? pathname : sidenavItems[0].href;

  useEffect(() => {
    const newItems = SIDE_NAV_ITEMS.filter((item) => {
      if (item.href === "manage_subscription" && !subscription?.subscriptionManagementUrl) {
        return false;
      }
      return checkPermission(item.permissionName, companyAdmin.userPermission);
    });
    setSideNavItems(newItems);

    if (!newItems.find((item) => item.href === selectedCategory)) {
      navigate(newItems[0].href);
    }
    setIsRequestLoading(false);
  }, [companyAdmin.userPermission, subscription]);

  useEffect(() => {
    if (pathname === "/kpi" && sidenavItems?.[0]?.href) navigate(sidenavItems[0].href);
  }, []);

  const renderSidenavItems = sidenavItems.map(({ label, href }, key) => {
    const itemKey = `item-${key}`;

    return (
      <MDBox key={itemKey} component="li" pt={key === 0 ? 0 : 1}>
        <MDTypography
          onClick={() => navigate(href)}
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          style={{ cursor: "pointer" }}
          sx={({
            borders: { borderRadius },
            functions: { pxToRem },
            palette: { light },
            transitions,
          }) => ({
            display: "flex",
            alignItems: "center",
            borderRadius: borderRadius.md,
            padding: `${pxToRem(10)} ${pxToRem(16)}`,
            transition: transitions.create("background-color", {
              easing: transitions.easing.easeInOut,
              duration: transitions.duration.shorter,
            }),

            "&:hover": {
              backgroundColor: light.main,
            },
          })}
        >
          {label}
        </MDTypography>
      </MDBox>
    );
  });

  const ComponentToRender = prebuiltComponents[selectedCategory] || null;

  return (
    <MDBox mt={4}>
      {isRequestLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={2}>
          {/* side nav */}
          <Card
            sx={{
              borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
              position: "sticky",
              top: "1%",
            }}
          >
            <MDBox
              component="ul"
              display="flex"
              flexDirection="column"
              p={2}
              m={0}
              sx={{ listStyle: "none" }}
            >
              {renderSidenavItems}
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={10}>
          <MDBox mb={3}>
            <ComponentToRender key={selectedCategory} />
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default KpiTabView;
